import { logger } from "../utils/logger/logger";
import {
  EmailLookupResponse,
  EmailLookupStatus,
  VerificationHelpRequestApiResponse,
} from "../../components/FormFields/EmailFirstCollectInfo/types";
import { getVerificationHelpUrl } from "./ApiUrls";
import { DatabaseId } from "../types/types";
import { fetchWithTimeout } from "./Network";
import { EventName, recordGaEvent } from "../GoogleAnalytics/ga";

const defaultJsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};
/**
 * Endpoint to support email-first functionality.
 * Look up a verification, return status and trigger notifier(s)
 *
 * @param programId Your programId from my.sheerid.com
 * @param emailAddress Email Address used for the verification.
 */
export const submitVerificationHelpRequest = async (
  programId: DatabaseId,
  emailAddress: string = undefined,
  headers = {},
): Promise<VerificationHelpRequestApiResponse> => {
  const url = getVerificationHelpUrl();
  const requestBody = {
    emailAddress,
    programId,
  };

  try {
    const response = await fetchWithTimeout(url, {
      headers: { ...defaultJsonHeaders, ...headers },
      method: "POST",
      body: JSON.stringify(requestBody),
    });

    const responseJson = await response.json();
    return { ...responseJson, status: response.status };
  } catch (error) {
    logger.error("Error in submitVerificationHelpRequest:", error);
    return {
      systemErrorMessage: "An error occurred during the request.",
      status: 500,
    };
  }
};

export async function handleSubmitVerificationHelpRequest(
  programId: string,
  email: string,
): Promise<EmailLookupResponse> {
  try {
    const resp: VerificationHelpRequestApiResponse = await submitVerificationHelpRequest(
      programId,
      email,
    );
    let status: EmailLookupStatus = EmailLookupStatus.PENDING;
    let message: string | null = null;

    if (resp.status === 500) {
      status = EmailLookupStatus.NOT_FOUND;
      message = "Error during lookup.";
    } else if (resp.status === 404 || resp.status === 429) {
      status = EmailLookupStatus.NOT_FOUND;
    } else if (resp.status === 400) {
      status = EmailLookupStatus.ALREADY_INITIATED;
    } else if (resp.status === 200) {
      if (resp.currentStep === "error" || resp.currentStep === "docReviewLimitExceeded") {
        status = EmailLookupStatus.NOT_FOUND;
        message = "An error occurred during verification.";
      } else if (
        resp.currentStep === "pending" ||
        resp.currentStep === "consolation" ||
        resp.currentStep === "loading"
      ) {
        status = EmailLookupStatus.PENDING;
        message = "pending";
      } else if (!resp.currentStep.toLowerCase().startsWith("collect")) {
        status = EmailLookupStatus.FOUND;
        message = resp.currentStep;
      } else {
        message = "Unexpected API response.";
      }
    }
    recordGaEvent({ eventName: EventName.VERIFICATION_HELP, params: { status, programId } });
    return { status, message };
  } catch (error) {
    logger.error("Error submitting verification help request:", error);
    return {
      status: EmailLookupStatus.PENDING,
      message: "Error during lookup.",
    };
  }
}
